import { useEffect, useRef, useState } from 'react'
// Aliased: the focus trap below listens for the DOM KeyboardEvent, not React's.
import type { KeyboardEvent as ReactKeyboardEvent } from 'react'

import type { PerilRecordDetail } from '../api/types'
import { formatAri, formatBushfireLevel, formatElevation, formatText, formatYesNo } from '../lib/peril'
import { MapPanel } from './MapPanel'
import { Button, Callout, StatusRow } from './ui'

interface DetailsModalProps {
  detail: PerilRecordDetail | null
  isLoading: boolean
  error: string | null
  /** Shown as the title while the record loads, so the modal is never unlabelled. */
  fallbackTitle: string | null
  onClose: () => void
  onRetry: () => void
}

type Row = [label: string, value: string]

type TabId = 'property' | 'map'

const TABS: Array<{ id: TabId; label: string }> = [
  { id: 'property', label: 'Property' },
  { id: 'map', label: 'Map' },
]

/**
 * The details view, as a centred modal titled with the peril address, matching
 * the legacy K2 form: a Property tab and a Map tab, Property first. Section
 * order and field labels are the legacy ones: Property Details, Address, Flood
 * Score, Bushfire Risk, then ARPC Perils.
 *
 * The legacy modal also carried a maximise button, which is dropped: the modal
 * is already as wide as the record needs.
 */
export function DetailsModal({
  detail,
  isLoading,
  error,
  fallbackTitle,
  onClose,
  onRetry,
}: DetailsModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  // The modal is mounted fresh on every open, so this always starts on Property.
  const [activeTab, setActiveTab] = useState<TabId>('property')

  useEffect(() => {
    closeRef.current?.focus()
  }, [])

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      // Keep focus inside the dialog while it is open.
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      const first = focusable?.[0]
      const last = focusable?.[focusable.length - 1]
      if (!first || !last) return

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // Left and right move between tabs, which is the expected tablist behaviour.
  function handleTabKeys(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const step = event.key === 'ArrowRight' ? 1 : -1
    const index = TABS.findIndex((tab) => tab.id === activeTab)
    const next = TABS[(index + step + TABS.length) % TABS.length]
    if (next) {
      setActiveTab(next.id)
      document.getElementById(`details-tab-${next.id}`)?.focus()
    }
  }

  const title = detail?.perilAddress ?? fallbackTitle ?? 'Peril record'
  const groups = detail ? buildGroups(detail) : []

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="details-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="modal__header">
          <h2 className="modal__title" id="details-modal-title">
            {title}
          </h2>
          <Button ref={closeRef} variant="secondary" size="small" onClick={onClose}>
            Close
          </Button>
        </div>

        {detail && !error ? (
          <div className="tabs" role="tablist" aria-label="Record views">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`details-tab-${tab.id}`}
                className="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`details-panel-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={handleTabKeys}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}

        <div className="modal__body">
          {error ? (
            <Callout onRetry={onRetry}>{error}</Callout>
          ) : isLoading || !detail ? (
            <StatusRow>Loading record details</StatusRow>
          ) : (
            <>
              <div
                role="tabpanel"
                id="details-panel-property"
                aria-labelledby="details-tab-property"
                hidden={activeTab !== 'property'}
              >
                {groups.map((group) => (
                  <section className="detail-group" key={group.title}>
                    <h3 className="detail-group__title">{group.title}</h3>
                    <dl className="detail-list">
                      {group.rows.map(([label, value]) => (
                        <div className="detail-list__row" key={label}>
                          <dt>{label}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                ))}
              </div>

              <div
                role="tabpanel"
                id="details-panel-map"
                aria-labelledby="details-tab-map"
                hidden={activeTab !== 'map'}
              >
                {/* Mounted only while selected, so the address is sent to Google
                    when it is asked for and not on every record opened. */}
                {activeTab === 'map' ? <MapPanel detail={detail} /> : null}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function buildGroups(detail: PerilRecordDetail): Array<{ title: string; rows: Row[] }> {
  const { property, address, flood, bushfire, arpc } = detail

  const addressRows: Row[] = [
    ['Unit Number', formatText(address.unitNumber)],
    ['Street Number From', formatText(address.streetNumberFrom)],
    ['Street Number To', formatText(address.streetNumberTo)],
    ['Street Name', formatText(address.streetName)],
    ['Street Type', formatText(address.streetType)],
    ['Suburb', formatText(address.suburb)],
    ['State', formatText(address.state)],
    ['Postcode', formatText(address.postcode)],
  ]
  // The legacy details view does not show these for an address search. They are
  // added only when the record carries them, so a Property search still sees
  // what it searched on. See docs/data-contract.md.
  if (address.propertyKey) addressRows.push(['Property Key', address.propertyKey])
  if (address.strataPlanNumber) {
    addressRows.push(['Strata Plan Number', address.strataPlanNumber])
  }

  return [
    {
      title: 'Property Details',
      rows: [
        ['GNAF PID', formatText(property.gnafPid)],
        ['Source', formatText(property.source)],
        ['Is Primary Reference', formatYesNo(property.isPrimaryReference)],
      ],
    },
    { title: 'Address', rows: addressRows },
    {
      title: 'Flood Score',
      rows: [
        ['Flood Score', formatText(flood.floodScore)],
        ['Flood ARI GL', formatAri(flood.floodAriGl)],
        ['Flood ARI GL 1M', formatAri(flood.floodAriGl1m)],
        ['Flood ARI GL 2M', formatAri(flood.floodAriGl2m)],
        ['Elevation', formatElevation(flood.elevation)],
      ],
    },
    {
      title: 'Bushfire Risk',
      rows: [
        ['Bushfire Attack Level', formatBushfireLevel(bushfire.attackLevel)],
        ['Bushfire Attack Level Category', formatText(bushfire.attackLevelCategory)],
      ],
    },
    {
      title: 'ARPC Perils',
      rows: [
        ['ARPC Wind Zone', formatText(arpc.windZone)],
        ['ARPC Wind Band', formatText(arpc.windBand)],
        ['ARPC Flood Band', formatText(arpc.floodBand)],
        ['ARPC Surge Band', formatText(arpc.surgeBand)],
      ],
    },
  ]
}
